import React, { useState, useEffect } from 'react';
import { Loader2, TrendingUp, Package, Users, DollarSign } from 'lucide-react';
import { vendorAPI } from '../../services/api';
import { Badge } from '../ui/badge';

const VendorReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    vendorAPI.getAnalytics()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!data) return <p className="text-center py-16 text-gray-400 text-sm">Failed to load vendor analytics</p>;

  const { vendors, summary } = data;

  return (
    <div className="space-y-5" data-testid="vendor-report">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-[#24549c]" /><span className="text-[10px] text-gray-500 font-medium">Active Vendors</span></div>
          <p className="text-xl font-bold text-gray-900" data-testid="vendor-count">{summary.total_vendors}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2"><Package className="w-4 h-4 text-emerald-600" /><span className="text-[10px] text-gray-500 font-medium">Total POs</span></div>
          <p className="text-xl font-bold text-gray-900" data-testid="total-pos">{summary.total_pos}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-amber-600" /><span className="text-[10px] text-gray-500 font-medium">Total PO Value</span></div>
          <p className="text-xl font-bold text-gray-900" data-testid="total-value">Rs. {summary.total_po_value?.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-blue-600" /><span className="text-[10px] text-gray-500 font-medium">Avg PO Value</span></div>
          <p className="text-xl font-bold text-gray-900">Rs. {summary.avg_po_value?.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Vendor Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] font-bold text-gray-500 uppercase" style={{ backgroundColor: '#f8f9fb' }}>
              <th className="text-left px-4 py-3">Vendor</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Location</th>
              <th className="text-right px-4 py-3">POs</th>
              <th className="text-right px-4 py-3">Total Value</th>
              <th className="text-right px-4 py-3">Avg Value</th>
              <th className="text-right px-4 py-3">Completed</th>
              <th className="text-right px-4 py-3">Pending</th>
              <th className="text-right px-4 py-3">Completion %</th>
              <th className="text-left px-4 py-3">Items Supplied</th>
            </tr>
          </thead>
          <tbody>
            {vendors.length === 0 ? (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-xs text-gray-400">No vendor data. Create purchase orders to see analytics.</td></tr>
            ) : vendors.map((v, i) => (
              <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/80 text-xs" data-testid={`vendor-row-${i}`}>
                <td className="px-4 py-3 font-semibold text-gray-900">{v.vendor_name}</td>
                <td className="px-4 py-3">
                  <Badge variant={v.vendor_type === 'import' ? 'default' : 'secondary'} className="text-[9px]">{v.vendor_type || '-'}</Badge>
                </td>
                <td className="px-4 py-3 text-gray-500">{v.location || '-'}</td>
                <td className="px-4 py-3 text-right font-semibold">{v.total_pos}</td>
                <td className="px-4 py-3 text-right font-bold" style={{ color: '#24549c' }}>Rs. {v.total_value?.toLocaleString('en-IN')}</td>
                <td className="px-4 py-3 text-right text-gray-600">Rs. {v.avg_value?.toLocaleString('en-IN')}</td>
                <td className="px-4 py-3 text-right text-emerald-600 font-semibold">{v.completed_pos}</td>
                <td className="px-4 py-3 text-right text-amber-600">{v.pending_pos}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${v.completion_rate}%`, backgroundColor: v.completion_rate >= 80 ? '#059669' : v.completion_rate >= 50 ? '#d97706' : '#dc2626' }} />
                    </div>
                    <span className="font-semibold">{v.completion_rate}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 max-w-[200px]">
                  <div className="flex flex-wrap gap-1">
                    {(v.items_supplied || []).slice(0, 3).map((item, j) => (
                      <Badge key={j} variant="outline" className="text-[8px] truncate max-w-[90px]">{item}</Badge>
                    ))}
                    {(v.items_supplied || []).length > 3 && <span className="text-[9px] text-gray-400">+{v.items_supplied.length - 3}</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VendorReport;
